import React, { useEffect, useState } from 'react';
import { generateGiftCodeBatch, getGiftCodeCampaignReport } from '../../services/adminService';
import { getPlans } from '../../services/planService';

const AdminGiftCodes = () => {
  const [plans, setPlans] = useState([]);
  const [planKey, setPlanKey] = useState('');
  const [quantity, setQuantity] = useState('5');
  const [campaignLabel, setCampaignLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [newCodes, setNewCodes] = useState(null);

  const [campaigns, setCampaigns] = useState([]);
  const [loadingReport, setLoadingReport] = useState(true);
  const [expandedCampaign, setExpandedCampaign] = useState(null);

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const data = await getGiftCodeCampaignReport();
      setCampaigns(data.campaigns);
    } catch (err) {
      // non-critical for the generation form itself
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPlans();
        setPlans(data.plans);
        if (data.plans.length > 0) setPlanKey(data.plans[0].plan_key);
      } catch (err) {
        // fine, dropdown just stays empty
      }
    };
    load();
    fetchReport();
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerateError('');
    setNewCodes(null);
    setGenerating(true);
    try {
      const data = await generateGiftCodeBatch({ planKey, quantity: Number(quantity), campaignLabel: campaignLabel.trim() });
      setNewCodes(data.codes);
      setCampaignLabel('');
      await fetchReport();
    } catch (err) {
      setGenerateError(err.response?.data?.message || 'Failed to generate codes');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(newCodes.join('\n'));
  };

  const daysAgo = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>🎁 Generate Promotional Gift Codes</h2>
        <p style={styles.subtitle}>
          For giveaways, influencer partnerships, or anything else where you're handing out free
          access. These are tracked separately from real purchases and show as "comped," not revenue.
        </p>

        <form onSubmit={handleGenerate} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Plan</label>
              <select value={planKey} onChange={(e) => setPlanKey(e.target.value)} style={styles.select}>
                {plans.map((p) => (
                  <option key={p.plan_key} value={p.plan_key}>{p.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Quantity</label>
              <input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.formField}>
            <label style={styles.fieldLabel}>Campaign label <span style={styles.hint}>(e.g. "Laurie - Food Blog")</span></label>
            <input
              type="text"
              placeholder="Who is this batch for?"
              value={campaignLabel}
              onChange={(e) => setCampaignLabel(e.target.value)}
              style={styles.input}
            />
          </div>
          {generateError && <p style={styles.error}>{generateError}</p>}
          <button type="submit" style={styles.generateBtn} disabled={generating || !planKey}>
            {generating ? 'Generating...' : 'Generate Codes'}
          </button>
        </form>

        {newCodes && (
          <div style={styles.newCodesBox}>
            <p style={styles.newCodesLabel}>{newCodes.length} code(s) generated — copy and send to your partner:</p>
            <textarea readOnly value={newCodes.join('\n')} style={styles.codesTextarea} rows={Math.min(newCodes.length, 8)} />
            <button onClick={handleCopyAll} style={styles.copyBtn}>Copy All</button>
          </div>
        )}
      </div>

      <div style={styles.card}>
        <h2 style={styles.title}>📊 Campaign Tracking</h2>
        <p style={styles.subtitle}>See which partnerships are actually driving redemptions.</p>

        {loadingReport ? (
          <p style={styles.status}>Loading...</p>
        ) : campaigns.length === 0 ? (
          <p style={styles.status}>No promotional codes generated yet.</p>
        ) : (
          campaigns.map((c) => {
            const rate = c.totalCodes > 0 ? Math.round((c.redeemedCount / c.totalCodes) * 100) : 0;
            const isExpanded = expandedCampaign === c.campaignLabel;
            return (
              <div key={c.campaignLabel} style={styles.campaignCard}>
                <div style={styles.campaignHeader} onClick={() => setExpandedCampaign(isExpanded ? null : c.campaignLabel)}>
                  <div>
                    <p style={styles.campaignName}>{c.campaignLabel}</p>
                    <p style={styles.campaignStats}>
                      {c.redeemedCount} / {c.totalCodes} redeemed ({rate}%)
                    </p>
                  </div>
                  <span style={styles.expandArrow}>{isExpanded ? '▲' : '▼'}</span>
                </div>
                {isExpanded && (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Code</th>
                        <th style={styles.th}>Plan</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Generated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.codes.map((code) => (
                        <tr key={code.code}>
                          <td style={styles.tdMono}>{code.code}</td>
                          <td style={styles.td}>{code.planKey}</td>
                          <td style={styles.td}>
                            {code.redeemed
                              ? <span style={styles.redeemedTag}>✓ Redeemed by {code.redeemedGroupName}</span>
                              : <span style={styles.unredeemedTag}>Unredeemed</span>}
                          </td>
                          <td style={styles.td}>{daysAgo(code.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '700px', margin: '0 auto' },
  card: {
    backgroundColor: 'white', padding: '32px', borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '24px',
  },
  title: { color: '#e67e22', marginBottom: '6px' },
  subtitle: { color: '#888', fontSize: '13px', marginBottom: '20px', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  formRow: { display: 'flex', gap: '14px' },
  formField: { flex: 1 },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' },
  hint: { fontWeight: '400', color: '#aaa', fontSize: '12px' },
  select: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', boxSizing: 'border-box', backgroundColor: 'white',
  },
  input: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', boxSizing: 'border-box',
  },
  generateBtn: {
    padding: '12px 24px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start',
  },
  newCodesBox: { marginTop: '20px', padding: '16px', backgroundColor: '#fdf0e3', borderRadius: '10px' },
  newCodesLabel: { color: '#a05a1a', fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
  codesTextarea: {
    width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #f0c896', fontSize: '13px',
    fontFamily: 'monospace', boxSizing: 'border-box', marginBottom: '10px', resize: 'vertical',
  },
  copyBtn: {
    padding: '8px 18px', backgroundColor: 'white', color: '#e67e22', border: '1px solid #e67e22',
    borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px',
  },
  campaignCard: { border: '1px solid #eee', borderRadius: '10px', marginBottom: '10px', overflow: 'hidden' },
  campaignHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', cursor: 'pointer' },
  campaignName: { color: '#333', fontWeight: '600', fontSize: '14px', marginBottom: '2px' },
  campaignStats: { color: '#888', fontSize: '12px' },
  expandArrow: { color: '#bbb', fontSize: '12px' },
  table: { width: '100%', borderCollapse: 'collapse', borderTop: '1px solid #f0f0f0' },
  th: { textAlign: 'left', padding: '8px 18px', fontSize: '11px', color: '#999', textTransform: 'uppercase' },
  td: { padding: '8px 18px', fontSize: '12px', color: '#444', borderTop: '1px solid #f7f7f7' },
  tdMono: { padding: '8px 18px', fontSize: '12px', color: '#444', borderTop: '1px solid #f7f7f7', fontFamily: 'monospace' },
  redeemedTag: { color: '#27ae60', fontWeight: '600' },
  unredeemedTag: { color: '#bbb' },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', fontSize: '13px' },
};

export default AdminGiftCodes;
